import { Activity as ActivityIcon, Search } from 'lucide-react';
import { useState } from 'react';
import { useAccountStore } from '../../stores/useAccountStore';
import type { ActivityEventType } from '../../types/account';
import s from './Pages.module.css';

type ActivityFilter = 'all' | ActivityEventType;

export function ActivityPage() {
  const activities = useAccountStore(a => a.activities);
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const [search, setSearch] = useState('');

  const sorted = [...activities].sort((a, b) => b.timestamp - a.timestamp);
  const filtered = sorted.filter(a => {
    if (filter !== 'all' && a.type !== filter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return a.title.toLowerCase().includes(q) || a.detail.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className={s.page}>
      <div className={s.pageHeader}>
        <h1 className={s.pageTitle}>Activity</h1>
        <p className={s.pageSubtitle}>Full activity feed across all operations.</p>
      </div>

      {/* Filter bar */}
      <div className={s.searchBar}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 10, color: 'var(--color-text-tertiary)' }} />
          <input
            className={s.searchInput}
            style={{ paddingLeft: 36 }}
            placeholder="Search activity..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {(['all', 'deployment', 'prefab-added', 'project-created', 'submission', 'error'] as ActivityFilter[]).map(f => (
          <button
            key={f}
            className={`${s.filterChip} ${filter === f ? s.filterActive : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : f.replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase())}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className={s.emptyState}>
          <div className={s.emptyIcon}><ActivityIcon size={48} /></div>
          <div className={s.emptyTitle}>No activity</div>
          <div className={s.emptyDesc}>Operations you perform will appear here.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(a => (
            <div key={a.id} style={{
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: 16,
            }}>
              <span className={`${s.badge} ${
                a.type === 'deployment' ? s.badgeInfo :
                a.type === 'earnings-claimed' ? s.badgeSuccess :
                a.type === 'subscription-created' || a.type === 'subscription-cancelled' ? s.badgeWarning :
                a.type === 'prefab-added' ? s.badgeInfo :
                a.type === 'error' ? s.badgeDanger : s.badgeNeutral
              }`}>
                {a.type.replace(/-/g, ' ')}
              </span>
              <span style={{ flex: 1, fontSize: 13, color: 'var(--color-text-primary)' }}>{a.title}</span>
              <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)', whiteSpace: 'nowrap' }}>
                {new Date(a.timestamp).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
